import { supabase } from './supabase';

export interface CoachRanking {
  coachId: string;
  coachName: string;
  completedAssessments: number;
}

export interface CodeRedemption {
  totalCodes: number;
  usedCodes: number;
  rate: number;
}

export interface MonthlyAssessment {
  month: string;
  label: string;
  count: number;
}

export interface RecentCompletion {
  id: string;
  respondentName: string;
  coachName: string;
  completedAt: string;
}

export interface TeamCompletion {
  teamId: string;
  teamName: string;
  totalMembers: number;
  completedMembers: number;
  rate: number;
}

export interface InactiveCoach {
  coachId: string;
  coachName: string;
  lastActivityDate: string | null;
}

export async function fetchTopCoaches(partnerId: string): Promise<CoachRanking[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id, users!coaches_user_id_fkey(full_name, email)')
    .eq('partner_id', partnerId);

  if (!coaches || coaches.length === 0) return [];

  const coachIds = coaches.map(c => c.id);

  const { data: results } = await supabase
    .from('results')
    .select('coach_id')
    .in('coach_id', coachIds);

  const countMap: Record<string, number> = {};
  (results || []).forEach(r => {
    if (r.coach_id) {
      countMap[r.coach_id] = (countMap[r.coach_id] || 0) + 1;
    }
  });

  return coaches
    .map(c => ({
      coachId: c.id,
      coachName: (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown',
      completedAssessments: countMap[c.id] || 0,
    }))
    .sort((a, b) => b.completedAssessments - a.completedAssessments)
    .slice(0, 10);
}

export async function fetchCodeRedemption(partnerId: string): Promise<CodeRedemption> {
  const { count: totalCodes } = await supabase
    .from('assessment_codes')
    .select('*', { count: 'exact', head: true })
    .eq('partner_id', partnerId);

  const { count: usedCodes } = await supabase
    .from('assessment_codes')
    .select('*', { count: 'exact', head: true })
    .eq('partner_id', partnerId)
    .eq('is_used', true);

  const total = totalCodes || 0;
  const used = usedCodes || 0;

  return {
    totalCodes: total,
    usedCodes: used,
    rate: total > 0 ? Math.round((used / total) * 100) : 0,
  };
}

export async function fetchAssessmentsOverTime(partnerId: string): Promise<MonthlyAssessment[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id')
    .eq('partner_id', partnerId);

  if (!coaches || coaches.length === 0) return [];

  const coachIds = coaches.map(c => c.id);

  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 5);
  sixMonthsAgo.setDate(1);
  sixMonthsAgo.setHours(0, 0, 0, 0);

  const { data: results } = await supabase
    .from('results')
    .select('completed_at')
    .in('coach_id', coachIds)
    .gte('completed_at', sixMonthsAgo.toISOString())
    .order('completed_at', { ascending: true });

  const monthMap: Record<string, number> = {};
  const months: { month: string; label: string }[] = [];

  for (let i = 0; i < 6; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - (5 - i));
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('default', { month: 'short' });
    months.push({ month: key, label });
    monthMap[key] = 0;
  }

  (results || []).forEach(r => {
    if (r.completed_at) {
      const d = new Date(r.completed_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (monthMap[key] !== undefined) {
        monthMap[key]++;
      }
    }
  });

  return months.map(m => ({
    month: m.month,
    label: m.label,
    count: monthMap[m.month],
  }));
}

export async function fetchRecentCompletions(partnerId: string): Promise<RecentCompletion[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id, users!coaches_user_id_fkey(full_name, email)')
    .eq('partner_id', partnerId);

  if (!coaches || coaches.length === 0) return [];

  const coachIds = coaches.map(c => c.id);
  const coachNameMap: Record<string, string> = {};
  coaches.forEach(c => {
    coachNameMap[c.id] = (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown';
  });

  const { data: results } = await supabase
    .from('results')
    .select('id, user_id, coach_id, completed_at, users!results_user_id_fkey(full_name, email)')
    .in('coach_id', coachIds)
    .order('completed_at', { ascending: false })
    .limit(15);

  return (results || []).map(r => ({
    id: r.id,
    respondentName: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
    coachName: r.coach_id ? coachNameMap[r.coach_id] || 'Unknown' : 'Unknown',
    completedAt: r.completed_at || '',
  }));
}

export async function fetchTeamCompletions(partnerId: string): Promise<TeamCompletion[]> {
  const { data: teams } = await supabase
    .from('teams')
    .select('id, name')
    .eq('partner_id', partnerId)
    .eq('is_active', true);

  if (!teams || teams.length === 0) return [];

  const teamIds = teams.map(t => t.id);

  const { data: members } = await supabase
    .from('team_members')
    .select('team_id, user_id')
    .in('team_id', teamIds);

  if (!members || members.length === 0) {
    return teams.map(t => ({
      teamId: t.id,
      teamName: t.name,
      totalMembers: 0,
      completedMembers: 0,
      rate: 0,
    }));
  }

  const teamMemberMap: Record<string, string[]> = {};
  members.forEach(m => {
    if (!teamMemberMap[m.team_id]) teamMemberMap[m.team_id] = [];
    teamMemberMap[m.team_id].push(m.user_id);
  });

  const allUserIds = [...new Set(members.map(m => m.user_id))];

  const { data: results } = await supabase
    .from('results')
    .select('user_id')
    .in('user_id', allUserIds);

  const usersWithResults = new Set((results || []).map(r => r.user_id));

  return teams.map(t => {
    const memberUserIds = teamMemberMap[t.id] || [];
    const total = memberUserIds.length;
    const completed = memberUserIds.filter(uid => usersWithResults.has(uid)).length;
    return {
      teamId: t.id,
      teamName: t.name,
      totalMembers: total,
      completedMembers: completed,
      rate: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  });
}

export interface CoachActivityStat {
  coachId: string;
  coachName: string;
  coachEmail: string;
  totalRespondents: number;
  completedAssessments: number;
  lastActiveDate: string | null;
}

export async function fetchCoachActivityStats(partnerId: string): Promise<CoachActivityStat[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id, users!coaches_user_id_fkey(full_name, email)')
    .eq('partner_id', partnerId);

  if (!coaches || coaches.length === 0) return [];

  const coachIds = coaches.map(c => c.id);

  const [respondentsRes, resultsRes] = await Promise.all([
    supabase
      .from('respondents')
      .select('coach_id')
      .in('coach_id', coachIds),
    supabase
      .from('results')
      .select('coach_id, completed_at')
      .in('coach_id', coachIds)
      .order('completed_at', { ascending: false }),
  ]);

  const respondentCountMap: Record<string, number> = {};
  (respondentsRes.data || []).forEach(r => {
    if (r.coach_id) {
      respondentCountMap[r.coach_id] = (respondentCountMap[r.coach_id] || 0) + 1;
    }
  });

  const completionCountMap: Record<string, number> = {};
  const lastActiveDateMap: Record<string, string | null> = {};
  coachIds.forEach(id => { lastActiveDateMap[id] = null; });
  (resultsRes.data || []).forEach(r => {
    if (r.coach_id) {
      completionCountMap[r.coach_id] = (completionCountMap[r.coach_id] || 0) + 1;
      if (!lastActiveDateMap[r.coach_id]) {
        lastActiveDateMap[r.coach_id] = r.completed_at;
      }
    }
  });

  return coaches.map(c => ({
    coachId: c.id,
    coachName: (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown',
    coachEmail: (c.users as any)?.email || '',
    totalRespondents: respondentCountMap[c.id] || 0,
    completedAssessments: completionCountMap[c.id] || 0,
    lastActiveDate: lastActiveDateMap[c.id],
  }));
}

export async function fetchInactiveCoaches(partnerId: string): Promise<InactiveCoach[]> {
  const { data: coaches } = await supabase
    .from('coaches')
    .select('id, users!coaches_user_id_fkey(full_name, email)')
    .eq('partner_id', partnerId)
    .eq('is_active', true);

  if (!coaches || coaches.length === 0) return [];

  const coachIds = coaches.map(c => c.id);

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const { data: recentResults } = await supabase
    .from('results')
    .select('coach_id, completed_at')
    .in('coach_id', coachIds)
    .order('completed_at', { ascending: false });

  const lastActivityMap: Record<string, string | null> = {};
  coachIds.forEach(id => { lastActivityMap[id] = null; });

  (recentResults || []).forEach(r => {
    if (r.coach_id && !lastActivityMap[r.coach_id]) {
      lastActivityMap[r.coach_id] = r.completed_at;
    }
  });

  return coaches
    .filter(c => {
      const lastDate = lastActivityMap[c.id];
      if (!lastDate) return true;
      return new Date(lastDate) < thirtyDaysAgo;
    })
    .map(c => ({
      coachId: c.id,
      coachName: (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown',
      lastActivityDate: lastActivityMap[c.id],
    }));
}
